<script>
import { cloneDeep, isEqual } from "lodash";
import { mapGetters } from "vuex";

export default {
  props: {
    data: {
      type: Object,
      required: true
    },
    editable: Boolean,
    hasAdminRole: Boolean
  },
  components: {
    Field: () => import("@/components/input-v.vue")
  },
  data() {
    const fieldErrors = new Set();
    return {
      fieldErrors,
      formData: {},
      opts: {
        empresasBeneficiadas: [],
        impactosFinanceiros: [],
        trackings: [],
        vidaUtilOpts: Array.from({ length: 14 }, (_, i) => {
          const value = i + 2;
          return {
            value,
            label: `${value} anos`
          };
        })
      },
      validationMode: "save"
    };
  },
  computed: {
    ...mapGetters(["clientId"]),
    fields() {
      const fields = [
        {
          key: "kpi",
          name: "KPI(s) definido(s) para iniciativa:",
          type: this.$fieldTypes.TEXTAREA,
          rules: [{ rule: "max", params: { size: 255 } }],
          editable: this.editable
        },
        {
          key: "impacto_financeiro",
          name: "Impacto Financeiro na Companhia:",
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: { to: "impactosFinanceiros", key: "id", name: "nome" },
          colSize: 6,
          editable: this.editable && this.opts.impactosFinanceiros.length > 0,
          placeholder: this.opts.impactosFinanceiros.length > 0 ? '' : 'Cadastre opções na tela de Configurações.'
        },
        {
          key: "empresa_beneficiada",
          name: "Empresa beneficiada pelo retorno financeiro:",
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: { to: "empresasBeneficiadas", key: "id", name: "nome" },
          colSize: 6,
          editable: this.editable && this.opts.empresasBeneficiadas.length > 0,
          placeholder: this.opts.empresasBeneficiadas.length > 0 ? '' : 'Cadastre opções na tela de Configurações.'
        },
        {
          key: "tracking",
          name: "Forma de Acompanhamento do Retorno Financeiro (TRACKING):",
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: { to: "trackings", key: "id", name: "nome" },
          colSize: 6,
          editable: this.editable && this.opts.trackings.length > 0,
          placeholder: this.opts.trackings.length > 0 ? '' : 'Cadastre opções na tela de Configurações.'
        },
        {
          key: "tracking_detalhamento",
          name:
            "Detalhamento de como será evidenciado o Retorno Financeiro (TRACKING):",
          type: this.$fieldTypes.TEXT,
          colSize: 6,
          editable: this.editable
        },
        {
          key: "vida_util",
          name:
            "Vida útil (período em anos em que o ativo gerado pela iniciativa apresentará beneficio financeiro):",
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: { to: "vidaUtilOpts", key: "value", name: "label" },
          colSize: 4,
          editable: this.editable
        },
        {
          key: "vida_util_justificativa",
          name:
            "Justificativa da expectativa de retorno financeiro assinalada no item anterior:",
          rules: [{ rule: "max", params: { size: 4000 } }],
          type: this.$fieldTypes.TEXTAREA,
          editable: this.editable
        }
      ];

      if (this.validationMode === "approve") {
        fields.forEach(field => {
          const rules = Array.isArray(field.rules) ? [...field.rules] : [];
          const isRequired = rules.some(({ rule }) => rule === 'required');

          if (isRequired || field.type === this.$fieldTypes.SWITCH) {
            return;
          }

          rules.push({ rule: "required" });
          field.rules = Array.from(rules);
        });
      }

      return fields;
    }
  },
  created() {
    this.setInternalData(this.data);
    this.getConfigs();
  },
  methods: {
    async getConfigs() {
      try {
        // Em ordem: impacto-financeiro, empresa-beneficiada, tracking
        const ids = [1, 2, 3];
        const resource = this.apiResource(`v1/ativacaoped/${this.clientId}/configuracoes`);
        const requests = ids.map((id) => {
          const query = `tipo=${id}`;
          return resource.get({ query });
        });
        const responses = await Promise.all(requests);
        const [impactosFinanceiros, empresasBeneficiadas, trackings] = responses.map(({ data }) => data);
        this.opts.empresasBeneficiadas = empresasBeneficiadas;
        this.opts.impactosFinanceiros = impactosFinanceiros;
        this.opts.trackings = trackings;
      } catch (error) {
        this.opts.empresasBeneficiadas = [];
        this.opts.impactosFinanceiros = [];
        this.opts.trackings = [];

        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    onError(key, invalid) {
      if (invalid) {
        this.fieldErrors.add(key);
      } else {
        this.fieldErrors.delete(key);
      }

      this.$emit("error", this.fieldErrors.size);
    },
    setInternalData(value) {
      if (!value) {
        return;
      }

      this.formData = cloneDeep(value);
    },
    /** @method validate
     * @param {'approve' | 'save'} mode
     * @returns {boolean}
     */
    validate(mode = "save") {
      this.validationMode = mode;
      const invalidFields = this.fields.filter(
        ({ hideInForm, valid }) => !hideInForm && !!valid && !valid()
      );
      return invalidFields.length === 0;
    }
  },
  watch: {
    data: {
      deep: true,
      handler(value) {
        if (!isEqual(this.formData, value)) {
          this.setInternalData(value);
        }
      }
    },
    formData: {
      deep: true,
      handler(value) {
        this.$emit("update:data", value);
      }
    }
  }
};
</script>

<template>
  <v-row class="pr-4">
    <Field
      v-for="(field, index) of fields"
      class="pl-4"
      :colSize="field.colSize"
      :key="`beneficio-economico-futuro-${index}`"
      :label="field"
      :opts="opts"
      :valid.sync="field.valid"
      v-model="formData[field.key]"
      @error="onError(field.key, $event)"
    ></Field>
  </v-row>
</template>
